import { useState, useRef, useEffect, useMemo } from 'react'
import { Search, MapPin, X } from 'lucide-react'
import { type UKLocation } from '../data/ukLocations'
import { loadCachedLocation } from '../hooks/useLocation'

/**
 * The ~25k-place UK list (OS Open Names) is large, so it's code-split and
 * lazy-loaded on first use rather than bundled into the main app. Cached
 * module-side so it loads at most once.
 */
let cachedLocations: UKLocation[] | null = null
let locationsPromise: Promise<UKLocation[]> | null = null
function loadStaticLocations(): Promise<UKLocation[]> {
  if (cachedLocations) return Promise.resolve(cachedLocations)
  if (!locationsPromise) {
    locationsPromise = import('../data/ukLocations').then((m) => {
      cachedLocations = m.UK_LOCATIONS
      return cachedLocations
    })
  }
  return locationsPromise
}

interface LocationSearchProps {
  onSelectLocation: (lat: number, lng: number, name?: string) => void
  onSearchPostcode: (postcode: string) => void
  isLoading?: boolean
  /** Prefill for the input (e.g. the q param from a shared URL) */
  initialValue?: string
}

/**
 * UK postcode validation regex
 */
const UK_POSTCODE_REGEX = /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i

/** UK bounding box for the geocoder (lonMin,latMin,lonMax,latMax) */
const UK_BBOX = '-8.7,49.8,2.0,61.0'

/** Debounce for remote geocoder lookups while typing */
const GEOCODE_DEBOUNCE_MS = 300

interface PhotonFeature {
  geometry: { coordinates: [number, number] }
  properties: {
    name?: string
    countrycode?: string
    osm_key?: string
    county?: string
    state?: string
  }
}

/**
 * Look up UK place names via the Photon (OSM) geocoder.
 * Fills the gaps in the static UK_LOCATIONS list (villages, suburbs, etc.).
 */
async function geocodePlaces(query: string, signal?: AbortSignal): Promise<UKLocation[]> {
  // Bias results toward the user's current location so small nearby places
  // (villages, suburbs) rank above bigger far-away name matches.
  const cached = loadCachedLocation()
  const bias = cached ? `&lat=${cached.lat}&lon=${cached.lng}` : ''
  const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(query)}&limit=10&lang=en&bbox=${UK_BBOX}${bias}`
  const response = await fetch(url, { signal })
  if (!response.ok) return []
  const data = (await response.json()) as { features?: PhotonFeature[] }

  const seen = new Set<string>()
  const results: UKLocation[] = []
  for (const f of data.features ?? []) {
    const p = f.properties
    // Populated places only (village/town/suburb/hamlet/city) — skip woods, POIs, etc.
    if (p.countrycode !== 'GB' || p.osm_key !== 'place' || !p.name) continue
    const region = p.county ?? p.state
    const key = `${p.name.toLowerCase()}|${region ?? ''}`
    if (seen.has(key)) continue
    seen.add(key)
    results.push({
      name: p.name,
      lat: f.geometry.coordinates[1],
      lng: f.geometry.coordinates[0],
      ...(region ? { region } : {}),
    })
    if (results.length >= 5) break
  }
  return results
}

/**
 * Location search with autocomplete for UK cities
 */
export function LocationSearch({
  onSelectLocation,
  onSearchPostcode,
  isLoading = false,
  initialValue = ''
}: LocationSearchProps) {
  const [value, setValue] = useState(initialValue)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [filteredLocations, setFilteredLocations] = useState<UKLocation[]>([])
  const [remoteLocations, setRemoteLocations] = useState<UKLocation[]>([])
  const [staticLocations, setStaticLocations] = useState<UKLocation[]>(cachedLocations ?? [])
  const containerRef = useRef<HTMLDivElement>(null)
  // After a selection the input value becomes the place name, which would
  // otherwise re-match itself and reopen the dropdown. This stays true until
  // the user types again (cleared in onChange). Starts true when the input is
  // prefilled from a restored/shared search term, so a refresh doesn't pop the
  // dropdown open for a value the user didn't just type.
  const suppressRef = useRef(initialValue.trim().length > 0)

  // Lazy-load the big UK place list once (kept out of the main bundle)
  useEffect(() => {
    let cancelled = false
    loadStaticLocations().then((locs) => { if (!cancelled) setStaticLocations(locs) })
    return () => { cancelled = true }
  }, [])

  // Filter locations based on input - only show when typing
  useEffect(() => {
    // A selection just set the value; don't reopen the dropdown
    if (suppressRef.current) {
      setFilteredLocations([])
      setShowSuggestions(false)
      return
    }

    if (!value.trim()) {
      setFilteredLocations([])
      setShowSuggestions(false)
      return
    }

    const search = value.toLowerCase().trim()

    // Check if it looks like a postcode - don't show city suggestions
    if (/^\d/.test(search) || UK_POSTCODE_REGEX.test(value.trim())) {
      setFilteredLocations([])
      setShowSuggestions(false)
      return
    }

    // Prefix matches first (Wolstanton for "wols"), then contains-matches
    const starts = staticLocations.filter((loc) => loc.name.toLowerCase().startsWith(search))
    const contains = staticLocations.filter(
      (loc) => !loc.name.toLowerCase().startsWith(search) && loc.name.toLowerCase().includes(search)
    )
    const filtered = [...starts, ...contains].slice(0, 6)

    setFilteredLocations(filtered)
    setShowSuggestions(filtered.length > 0)
  }, [value, staticLocations])

  // Remote geocoder fallback for places the static list doesn't know
  // (villages, suburbs, hamlets). Debounced; aborted on each keystroke.
  useEffect(() => {
    const search = value.trim()
    if (suppressRef.current || search.length < 3 || /^\d/.test(search) || UK_POSTCODE_REGEX.test(search)) {
      setRemoteLocations([])
      return
    }

    const controller = new AbortController()
    const timer = setTimeout(async () => {
      try {
        const places = await geocodePlaces(search, controller.signal)
        setRemoteLocations(places)
        if (places.length > 0) setShowSuggestions(true)
      } catch {
        // Aborted or geocoder unavailable — static suggestions still work
      }
    }, GEOCODE_DEBOUNCE_MS)

    return () => {
      controller.abort()
      clearTimeout(timer)
    }
  }, [value])

  // Local matches first, then remote ones that aren't already listed
  const suggestions = useMemo(() => {
    const localNames = new Set(filteredLocations.map((l) => l.name.toLowerCase()))
    return [
      ...filteredLocations,
      ...remoteLocations.filter((r) => !localNames.has(r.name.toLowerCase())),
    ].slice(0, 10)
  }, [filteredLocations, remoteLocations])

  // Close suggestions when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSubmit = async () => {
    const trimmed = value.trim()
    if (!trimmed) return

    // A submit resolves to a location/postcode; keep the dropdown closed
    suppressRef.current = true
    setFilteredLocations([])
    setRemoteLocations([])
    setShowSuggestions(false)

    // Check if it's a valid postcode
    if (UK_POSTCODE_REGEX.test(trimmed)) {
      onSearchPostcode(trimmed)
      return
    }

    // Exact match in the static list wins
    const list = await loadStaticLocations()
    const location = list.find(
      (loc) => loc.name.toLowerCase() === trimmed.toLowerCase()
    )
    if (location) {
      onSelectLocation(location.lat, location.lng, location.name)
      setValue(location.name)
      setShowSuggestions(false)
      return
    }

    // Otherwise take the top suggestion (local or remote)
    if (suggestions.length > 0) {
      onSelectLocation(suggestions[0].lat, suggestions[0].lng, suggestions[0].name)
      setValue(suggestions[0].name)
      setShowSuggestions(false)
      return
    }

    // Last resort: one-off geocoder lookup (debounced fetch may not have landed)
    try {
      const places = await geocodePlaces(trimmed)
      if (places.length > 0) {
        onSelectLocation(places[0].lat, places[0].lng, places[0].name)
        setValue(places[0].name)
        setShowSuggestions(false)
      }
    } catch {
      // Geocoder unavailable — keep current behavior (no-op)
    }
  }

  const handleLocationSelect = (location: UKLocation) => {
    suppressRef.current = true
    setValue(location.name)
    onSelectLocation(location.lat, location.lng, location.name)
    setFilteredLocations([])
    setRemoteLocations([])
    setShowSuggestions(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSubmit()
    } else if (e.key === 'Escape') {
      setShowSuggestions(false)
    }
  }

  return (
    <div className="relative flex-1" ref={containerRef}>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <input
            type="text"
            value={value}
            onChange={(e) => { suppressRef.current = false; setValue(e.target.value) }}
            onFocus={() => !suppressRef.current && value.trim() && suggestions.length > 0 && setShowSuggestions(true)}
            onKeyDown={handleKeyDown}
            placeholder="City or postcode"
            disabled={isLoading}
            className="w-full pl-9 pr-8 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:opacity-50"
          />
          <MapPin
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          {value && (
            <button
              type="button"
              onClick={() => {
                setValue('')
                setShowSuggestions(false)
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 p-0.5"
              aria-label="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>
        <button
          onClick={handleSubmit}
          disabled={isLoading || !value.trim()}
          className="px-3 py-2 bg-brand-500 hover:bg-brand-600 disabled:bg-brand-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center justify-center"
          aria-label="Search"
        >
          <Search size={18} />
        </button>
      </div>

      {/* Autocomplete suggestions */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-12 mt-1 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 max-h-60 overflow-y-auto z-50">
          {suggestions.map((location) => (
            <button
              key={`${location.name}-${location.lat}`}
              onClick={() => handleLocationSelect(location)}
              className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center justify-between"
            >
              <span className="text-slate-900 dark:text-white font-medium">
                {location.name}
              </span>
              {location.region && (
                <span className="text-slate-400 dark:text-slate-500 text-xs">
                  {location.region}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
